import {
  Icon, Select, Tooltip, Typography,
} from '@gupshup-frontend/gupshup-ts-components';
import { friendlyError } from './errorMessages';

// Transcribed verbatim (order + casing) from gs-intg-services'
// src/modules/WhatsApp/enterprise-template/constants/template-languages.constant.ts,
// GENERAL_ALLOWED_LANGUAGES, verified 2026-09-22. ValidationUtils.validateLanguage on that side
// compares by String.equals (case-sensitive), so the casing below must match exactly
// (e.g. "zh_CN", never "zh_cn").
//
// That same file also exports AUTH_ALLOWED_LANGUAGES for Authentication-category templates —
// diffed against GENERAL_ALLOWED_LANGUAGES on 2026-09-22 and it is the identical 68-code SET,
// just in a different array order. One list is therefore correct for every category, including
// Authentication; there is no second list to swap in.
//
// The Gupshup console's language picker shows more entries than this list — deliberately not
// matched here. The console submits through a different path than we do, so its list isn't
// authoritative for ours, and any code outside this 68 fails ValidationUtils.validateLanguage
// upstream (String.equals against the constant, so an unlisted code is rejected outright).
// Verify any addition against gs-intg-services'
// src/modules/WhatsApp/enterprise-template/constants/template-languages.constant.ts first —
// don't copy an entry from the console just because it's offered there.
const LANGUAGE_CODES = [
  ['af', 'Afrikaans'],
  ['sq', 'Albanian'],
  ['ar', 'Arabic'],
  ['az', 'Azerbaijani'],
  ['bn', 'Bengali'],
  ['bg', 'Bulgarian'],
  ['ca', 'Catalan'],
  ['zh_CN', 'Chinese (CHN)'],
  ['zh_HK', 'Chinese (HKG)'],
  ['zh_TW', 'Chinese (TAI)'],
  ['hr', 'Croatian'],
  ['cs', 'Czech'],
  ['da', 'Danish'],
  ['nl', 'Dutch'],
  ['en', 'English'],
  ['en_GB', 'English (UK)'],
  ['en_US', 'English (US)'],
  ['et', 'Estonian'],
  ['fil', 'Filipino'],
  ['fi', 'Finnish'],
  ['fr', 'French'],
  ['de', 'German'],
  ['el', 'Greek'],
  ['gu', 'Gujarati'],
  ['ha', 'Hausa'],
  ['he', 'Hebrew'],
  ['hi', 'Hindi'],
  ['hu', 'Hungarian'],
  ['id', 'Indonesian'],
  ['ga', 'Irish'],
  ['it', 'Italian'],
  ['ja', 'Japanese'],
  ['kn', 'Kannada'],
  ['kk', 'Kazakh'],
  ['ko', 'Korean'],
  ['lo', 'Lao'],
  ['lv', 'Latvian'],
  ['lt', 'Lithuanian'],
  ['mk', 'Macedonian'],
  ['ms', 'Malay'],
  ['ml', 'Malayalam'],
  ['mr', 'Marathi'],
  ['nb', 'Norwegian'],
  ['fa', 'Persian'],
  ['pl', 'Polish'],
  ['pt_BR', 'Portuguese (BR)'],
  ['pt_PT', 'Portuguese (POR)'],
  ['pa', 'Punjabi'],
  ['ro', 'Romanian'],
  ['ru', 'Russian'],
  ['sr', 'Serbian'],
  ['sk', 'Slovak'],
  ['sl', 'Slovenian'],
  ['es', 'Spanish'],
  ['es_AR', 'Spanish (ARG)'],
  ['es_ES', 'Spanish (SPA)'],
  ['es_MX', 'Spanish (MEX)'],
  ['sw', 'Swahili'],
  ['sv', 'Swedish'],
  ['ta', 'Tamil'],
  ['te', 'Telugu'],
  ['th', 'Thai'],
  ['tr', 'Turkish'],
  ['uk', 'Ukrainian'],
  ['ur', 'Urdu'],
  ['uz', 'Uzbek'],
  ['vi', 'Vietnamese'],
  ['zu', 'Zulu'],
];

const LANGUAGE_ITEMS = LANGUAGE_CODES.map(([value, label]) => ({
  value,
  // Console shows the language label alone (e.g. "Afrikaans"), no appended code.
  label,
  'data-testid': `bloomreach-templates-form-language-option-${value}`,
}));

// 3. Language — Select, case-sensitive on the wire. Locked in edit mode for the same reason as
// TemplateName: upstream overwrites it from the live Meta template and UpdateTemplateDto rejects
// the key outright, so Form/payload.js never sends it on an edit.
const LanguageSection = ({ state, dispatch, errors, disabled, isEdit }) => {
  const handleChange = (selected) => {
    const value = Array.isArray(selected) ? selected[0] : selected;
    dispatch({ type: 'merge', path: 'language', value: { value, error: '' } });
  };

  return (
    <div className="br-tpl-form-card" data-testid="bloomreach-templates-form-language">
      <div className="br-tpl-form-card-heading-row">
        <div className="br-tpl-form-card-heading">
          <Typography variant="subtitle1" data-testid="bloomreach-templates-form-language-heading">
            Language*
          </Typography>
        </div>
        <Tooltip
          placement="left"
          title="You will need to specify the language in which the message template is submitted"
          data-testid="bloomreach-templates-form-language-tooltip"
        >
          <span>
            <Icon name="question" size={18} showCursor />
          </span>
        </Tooltip>
      </div>
      <Typography variant="caption" className="br-tpl-form-helper-text">
        Choose which languages your message template will be sent in.
      </Typography>
      <Select
        label="Select"
        items={LANGUAGE_ITEMS}
        value={state.language.value}
        handleChange={handleChange}
        showSearch
        width="100%"
        maxHeightOfDropdown="300px"
        disabled={disabled || isEdit}
        data-testid="bloomreach-templates-form-language-select"
      />
      {errors['language.value'] && (
        <Typography variant="caption" color="error" data-testid="bloomreach-templates-form-language-error">
          {friendlyError(errors['language.value'])}
        </Typography>
      )}
    </div>
  );
};

export default LanguageSection;
